import { useState, useRef, useEffect } from 'react';
import {
  Box, Button, TextField, MenuItem, Select, InputLabel,
  FormControl, Typography, Slider, Grid, Paper
} from '@mui/material';
import { Delete, Download } from '@mui/icons-material';
import { SIGNATURE_FONTS, SIGNATURE_COLORS } from './signatureConstants';

export default function SignatureTyper({ width, height, onCapture, disableSignAndSave }) {
  const [text, setText] = useState('');
  const [selectedFont, setSelectedFont] = useState(SIGNATURE_FONTS[0].value);
  const [selectedColor, setSelectedColor] = useState(SIGNATURE_COLORS[0].value);
  const [fontSize, setFontSize] = useState(48);

  const canvasRef = useRef(null);
  const ctxRef = useRef(null);

  useEffect(() => {
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctxRef.current = ctx;
      drawSignature();
    }
  }, []);

  useEffect(() => {
    drawSignature();
  }, [text, selectedFont, selectedColor, fontSize]);

  const drawSignature = () => {
    if (!canvasRef.current || !ctxRef.current) return;

    const ctx = ctxRef.current;
    const canvas = canvasRef.current;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (!text.trim()) return;

    ctx.font = `${fontSize}px ${selectedFont}`;
    ctx.fillStyle = selectedColor;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    ctx.fillText(text, canvas.width / 2, canvas.height / 2);
  };

  const trimCanvas = (canvas) => {
    const ctx = canvas.getContext("2d");
    const width = canvas.width;
    const height = canvas.height;
    const imageData = ctx.getImageData(0, 0, width, height);
    const data = imageData.data;

    let top = null, bottom = null, left = null, right = null;

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const alpha = data[(y * width + x) * 4 + 3];
        if (alpha > 0) {
          if (top === null) top = y;
          bottom = y;
          if (left === null || x < left) left = x;
          if (right === null || x > right) right = x;
        }
      }
    }

    if (top === null) return null; // Empty

    const trimmedWidth = right - left + 1;
    const trimmedHeight = bottom - top + 1;
    const trimmedCanvas = document.createElement("canvas");
    trimmedCanvas.width = trimmedWidth;
    trimmedCanvas.height = trimmedHeight;
    const trimmedCtx = trimmedCanvas.getContext("2d");

    trimmedCtx.putImageData(
      ctx.getImageData(left, top, trimmedWidth, trimmedHeight),
      0,
      0
    );

    return trimmedCanvas;
  };

  const handleCapture = () => {
    if (canvasRef.current && text.trim()) {
      const trimmedCanvas = trimCanvas(canvasRef.current);
      const data = trimmedCanvas.toDataURL("image/png");
      onCapture(data);
    }
  };

  const handleClear = () => {
    setText('');
    if (ctxRef.current) {
      ctxRef.current.clearRect(0, 0, width, height);
    }
  };

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Type Your Signature"
            value={text}
            onChange={(e) => setText(e.target.value)}
            fullWidth
            variant="outlined"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth variant="outlined">
            <InputLabel>Font</InputLabel>
            <Select
              value={selectedFont}
              onChange={(e) => setSelectedFont(e.target.value)}
              label="Font"
            >
              {SIGNATURE_FONTS.map((font) => (
                <MenuItem key={font.name} value={font.value} style={{ fontFamily: font.value }}>
                  {font.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth variant="outlined">
            <InputLabel>Color</InputLabel>
            <Select
              value={selectedColor}
              onChange={(e) => setSelectedColor(e.target.value)}
              label="Color"
            >
              {SIGNATURE_COLORS.map((color) => (
                <MenuItem key={color.name} value={color.value}>
                  {color.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography gutterBottom>Font Size: {fontSize}px</Typography>
          <Slider
            value={fontSize}
            onChange={(e, value) => setFontSize(value)}
            min={24}
            max={72}
            step={1}
          />
        </Grid>
      </Grid>

      <Paper
        variant="outlined"
        sx={{
          position: 'relative',
          height: height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#fafafa'
        }}
      >
        <canvas ref={canvasRef} width={width} height={height} style={{ width: '100%', height: '100%' }} />
        {!text && (
          <Typography
            variant="subtitle1"
            color="text.secondary"
            sx={{ position: 'absolute' }}
          >
            Your signature will appear here
          </Typography>
        )}
      </Paper>

      <Box display="flex" justifyContent="flex-end" gap={2}>
        <Button
          variant="outlined"
          color="error"
          startIcon={<Delete />}
          onClick={handleClear}
          disabled={!text}
        >
          Clear
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Download />}
          onClick={handleCapture}
          disabled={!text}
        >
          Capture
        </Button>
      </Box>

      {/* Font Preview Section */}
      {!disableSignAndSave && <Box>
        <Typography variant="subtitle1" gutterBottom>
          Font Preview
        </Typography>
        <Grid container spacing={2}>
          {SIGNATURE_FONTS.map((font) => (
            <Grid item xs={12} md={6} key={font.name}>
              <Paper
                onClick={() => setSelectedFont(font.value)}
                elevation={selectedFont === font.value ? 4 : 1}
                sx={{
                  p: 2,
                  cursor: 'pointer',
                  border: selectedFont === font.value ? '2px solid #1976d2' : '1px solid #ccc',
                  fontFamily: font.value,
                }}
              >
                <Typography sx={{ fontSize: 20 }}>{text || 'Your Signature'}</Typography>
                <Typography variant="caption" color="text.secondary">{font.name}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
      }
    </Box>
  );
}